import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Shield, FileText, Bot, Award, ArrowRight, RefreshCw, AlertTriangle, CheckCircle, ExternalLink } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BLOCKS = [
  { key: 'site_foundation', label: 'Site Foundation', desc: 'Can search engines reach your site?', icon: Shield, color: '#3B82F6' },
  { key: 'page_quality', label: 'Page Quality', desc: 'Can search engines understand your page?', icon: FileText, color: '#8B5CF6' },
  { key: 'ai_ready', label: 'AI-Ready Content', desc: 'Can AI cite your content?', icon: Bot, color: '#F59E0B' },
  { key: 'ai_authority', label: 'AI Authority', desc: 'Does AI know your brand exists?', icon: Award, color: '#EF4444' },
];

const PublicAudit = () => {
  const navigate = useNavigate();
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const runAudit = async (e) => {
    e.preventDefault();
    if (!url.trim()) return;
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const res = await axios.post(`${API_URL}/api/audit/basic`, { url: url.trim() });
      setResult(res.data);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Could not analyze this URL. Please check and try again.';
      setError(detail);
    } finally {
      setLoading(false);
    }
  };

  const overallScore = result?.summary?.overall_score || 0;
  const totalIssues = result?.summary?.total_issues || 0;
  const totalChecks = result?.meta?.check_count || 0;
  const scanTime = ((result?.meta?.scan_time_ms || 0) / 1000).toFixed(1);

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-[#E2DDD8]">
        <div className="max-w-5xl mx-auto px-4 py-4 flex justify-between items-center">
          <a href="/" className="flex items-center gap-2">
            <img src="/Logo_A.svg" alt="ARPAI GEO" className="h-8" />
            <span className="text-lg font-bold tracking-tight text-[#2F2E2F]">ARPAI GEO</span>
          </a>
          <div className="flex gap-3">
            <button onClick={() => navigate('/login')} className="text-xs font-bold tracking-widest uppercase px-4 py-2 border border-[#E2DDD8] text-slate-700 hover:bg-slate-50">Log in</button>
            <button onClick={() => navigate('/register')} className="text-xs font-bold tracking-widest uppercase px-4 py-2 bg-[#1BB594] text-white hover:bg-[#159A7F]">Sign up free</button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-12">
        {/* Hero */}
        <div className="text-center mb-10">
          <h1 className="text-4xl sm:text-5xl font-black tracking-tight text-[#2F2E2F] mb-4">
            Is your site ready for AI search?
          </h1>
          <p className="text-lg text-slate-500 max-w-2xl mx-auto">
            {totalChecks || '97'} checks across SEO fundamentals, page quality, AI-readiness, and brand authority. Free, instant, no signup.
          </p>
        </div>

        {/* Form */}
        <form onSubmit={runAudit} className="max-w-xl mx-auto mb-12">
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="Enter your website URL..."
                className="w-full pl-10 pr-4 py-3 border border-[#E2DDD8] text-sm focus:outline-none focus:border-[#1BB594]"
                disabled={loading}
              />
            </div>
            <button
              type="submit"
              disabled={loading || !url.trim()}
              className="px-6 py-3 bg-[#1BB594] text-white font-bold tracking-widest uppercase text-xs hover:bg-[#159A7F] disabled:opacity-50 flex items-center gap-2 flex-shrink-0"
            >
              {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              {loading ? 'Scanning...' : 'Audit'}
            </button>
          </div>
          {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
        </form>

        {/* Loading state */}
        {loading && (
          <div className="text-center py-12">
            <RefreshCw className="w-8 h-8 text-[#1BB594] animate-spin mx-auto mb-4" />
            <p className="text-sm text-slate-500">Analyzing your site across {totalChecks || '97'} checks...</p>
            <p className="text-xs text-slate-400 mt-1">This usually takes 3-5 seconds</p>
          </div>
        )}

        {/* Results */}
        {result && !loading && (
          <div className="max-w-3xl mx-auto">
            {/* Overall score */}
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-4 p-6 border border-[#E2DDD8]">
                <div className="text-6xl font-black" style={{ color: overallScore >= 75 ? '#1BB594' : overallScore >= 50 ? '#F59E0B' : '#EF4444' }}>
                  {overallScore}<span className="text-3xl text-slate-300">%</span>
                </div>
                <div className="text-left">
                  <p className="text-sm font-semibold text-slate-800">Overall Score</p>
                  <p className="text-xs text-slate-500">{totalChecks} checks · {scanTime}s</p>
                  <div className="flex gap-2 mt-1">
                    {totalIssues > 0 && (
                      <span className="text-[10px] px-1.5 py-0.5 bg-red-100 text-red-600 font-bold">{totalIssues} issues</span>
                    )}
                    <span className="text-[10px] px-1.5 py-0.5 bg-green-100 text-green-600 font-bold">
                      {(totalChecks - totalIssues)} passed
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Score bars */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
              {BLOCKS.map(block => {
                const data = result.summary?.blocks?.[block.key] || {};
                const score = data.score || 0;
                const barColor = score >= 75 ? '#1BB594' : score >= 50 ? '#F59E0B' : '#EF4444';
                const Icon = block.icon;
                return (
                  <div key={block.key} className="p-5 border border-[#E2DDD8]">
                    <div className="flex items-center gap-2 mb-3">
                      <Icon className="w-5 h-5" style={{ color: block.color }} />
                      <div className="flex-1">
                        <div className="flex justify-between items-baseline">
                          <p className="text-sm font-bold text-slate-800">{block.label}</p>
                          <span className="text-xl font-black" style={{ color: barColor }}>{score}%</span>
                        </div>
                        <p className="text-[10px] text-slate-400">{block.desc}</p>
                      </div>
                    </div>
                    <div className="w-full h-2.5 bg-slate-100 overflow-hidden">
                      <div className="h-full transition-all duration-700" style={{ width: `${score}%`, backgroundColor: barColor }} />
                    </div>
                    <div className="flex justify-between mt-1.5">
                      {(data.fail || 0) > 0 && (
                        <span className="text-[10px] text-red-500 flex items-center gap-0.5">
                          <AlertTriangle className="w-3 h-3" /> {data.fail} issues
                        </span>
                      )}
                      {(data.pass || 0) > 0 && (
                        <span className="text-[10px] text-green-500 flex items-center gap-0.5 ml-auto">
                          <CheckCircle className="w-3 h-3" /> {data.pass} passed
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* CTA */}
            <div className="text-center p-8 border-2 border-[#1BB594] bg-emerald-50/30">
              <h2 className="text-xl font-bold text-[#2F2E2F] mb-2">
                {totalIssues > 0
                  ? `Found ${totalIssues} issues. Want to see what to fix?`
                  : 'Want detailed insights and ongoing monitoring?'
                }
              </h2>
              <p className="text-sm text-slate-500 mb-4">
                Sign up to see every issue with step-by-step fix instructions, track your progress over time, and monitor your AI visibility across 8 engines.
              </p>
              <button
                onClick={() => navigate('/register')}
                className="px-8 py-3 bg-[#1BB594] text-white font-bold tracking-widest uppercase text-xs hover:bg-[#159A7F] inline-flex items-center gap-2"
              >
                Sign up free <ArrowRight className="w-4 h-4" />
              </button>
              <p className="text-[10px] text-slate-400 mt-3">No credit card required · Full audit details after signup</p>
            </div>

            {/* URL badge */}
            <div className="mt-6 text-center">
              <a href={result.url} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-slate-600">
                <ExternalLink className="w-3 h-3" /> {result.url}
              </a>
            </div>
          </div>
        )}

        {/* Pre-result info (show only when no result) */}
        {!result && !loading && (
          <div className="max-w-3xl mx-auto grid grid-cols-1 sm:grid-cols-2 gap-6 mt-8">
            {BLOCKS.map(block => {
              const Icon = block.icon;
              return (
                <div key={block.key} className="p-5 border border-[#E2DDD8]">
                  <div className="flex items-center gap-3 mb-2">
                    <Icon className="w-5 h-5" style={{ color: block.color }} />
                    <p className="text-sm font-bold text-slate-800">{block.label}</p>
                  </div>
                  <p className="text-xs text-slate-500">{block.desc}</p>
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-[#E2DDD8] py-8 mt-16">
        <div className="max-w-5xl mx-auto px-4 flex flex-wrap justify-between items-center gap-4">
          <p className="text-xs text-slate-400">&copy; 2026 ARPAI GEO. All rights reserved.</p>
          <div className="flex gap-4 text-xs text-slate-400">
            <a href="/privacy" className="hover:text-slate-600">Privacy</a>
            <a href="/terms" className="hover:text-slate-600">Terms</a>
            <a href="/page-free-audit.html" className="hover:text-slate-600">Free AI Audit</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PublicAudit;
